import {
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import React from 'react';
import { DeviceDesktop } from 'tabler-icons-react';

import { useGetSessions, useRevokeSession } from 'api/sessions';
import type { Session } from 'api/sessions';

type SessionsModalProps = {
  opened: boolean;
  onClose: () => void;
};

function formatSession(session: Session): string {
  const date = session.createdAt
    ? new Date(session.createdAt).toLocaleString()
    : 'Unknown date';
  const ua = session.userAgent || 'Unknown device';
  return `${ua} · ${date}`;
}

export default function SessionsModal({ opened, onClose }: SessionsModalProps) {
  const { data, isLoading } = useGetSessions();
  const revokeMutation = useRevokeSession();
  const sessions = data?.sessions ?? [];

  const handleRevoke = (sessionId: string) => {
    revokeMutation.mutate(sessionId, {
      onSuccess: () => {
        showNotification({
          title: 'Session revoked',
          message: 'That device will be signed out on next request.',
          color: 'green',
        });
      },
      onError: () => {
        showNotification({
          title: 'Error',
          message: 'Could not revoke session.',
          color: 'red',
        });
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Title order={3}>Active sessions</Title>}
      size="md"
      aria-labelledby="sessions-modal-title"
    >
      <Stack spacing="sm">
        <Text size="sm" color="dimmed">
          Devices where you are signed in. Revoke to sign out that device.
        </Text>
        {isLoading && (
          <Text size="sm" color="dimmed">
            Loading…
          </Text>
        )}
        {!isLoading && sessions.length === 0 && (
          <Text size="sm" color="dimmed">
            No sessions found.
          </Text>
        )}
        {!isLoading && sessions.length > 0 && (
          <Stack spacing="xs">
            {sessions.map((session) => (
              <Group key={session._id} position="apart" noWrap>
                <Group spacing="sm" noWrap style={{ minWidth: 0 }}>
                  <DeviceDesktop size={18} style={{ flexShrink: 0 }} />
                  <Text size="sm" lineClamp={2}>
                    {formatSession(session)}
                  </Text>
                </Group>
                <Button
                  size="xs"
                  variant="light"
                  color="red"
                  onClick={() => handleRevoke(session._id)}
                  loading={revokeMutation.isLoading}
                  disabled={revokeMutation.isLoading}
                >
                  Revoke
                </Button>
              </Group>
            ))}
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
